"use client";

import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chip } from "primereact/chip";
import Loader from "./loader";
import styles from "./FabricDetail.module.scss";

const FabricDetail = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [fabric, setFabric] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const item = localStorage.getItem("selectedFabric");
    if (item) {
      try {
        console.log("Cargando tela desde localStorage:", item);
        setFabric(JSON.parse(item));
        setLoading(false);
      } catch (error) {
        console.error("Error parsing selectedFabric:", error);
        setFabric(null);
      }
    } else {
      setFabric(null);
    }
    setLoading(false);
  }, [code]);

  // Hooks unconditionally en el tope
  const getRestrictionSeverity = (restriction) => {
    switch (restriction) {
      case "Sin restricción":
        return "success";
      case "Bajo Programación":
        return "warning";
      case "Restricción":
        return "danger";
      default:
        return "info";
    }
  };

  const finishes = useMemo(() => {
    const { finishes } = fabric || {};
    if (!fabric?.finishes) return [];
    const rawFinishes = finishes;
    if (Array.isArray(rawFinishes)) {
      return rawFinishes
        .filter((f) => f !== null && f !== undefined && String(f).trim() !== "")
        .map((f) => String(f).trim());
    }
    if (rawFinishes && typeof rawFinishes === "object") {
      return Object.values(rawFinishes)
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map((v) => String(v).trim());
    }
    return [];
  }, [fabric?.finishes]);

  const compositionChips = useMemo(() => {
    if (!fabric?.composition) return [];
    return fabric.composition.split(" - ").map((comp) => {
      const match = comp.match(/^(.+?)\s*(\d+%)?$/);
      return {
        material: match ? match[1].trim() : comp,
        percentage: match && match[2] ? match[2] : null,
      };
    });
  }, [fabric?.composition]);

  const priceTableData = useMemo(() => {
    if (!fabric?.prices) return [];
    return Object.entries(fabric.prices).map(([lista, precios]) => ({
      lista,
      blancoKg: precios.whiteKg,
      blancoM: precios.whiteM,
      clarosKg: precios.lightKg,
      clarosM: precios.lightM,
      oscurosKg: precios.darkKg,
      oscurosM: precios.darkM,
    }));
  }, [fabric?.prices]);

  const priceTemplate = (value) => {
    if (typeof value !== "number" || isNaN(value)) return "$-";
    return <span className={styles.priceValue}>${value.toLocaleString()}</span>;
  };

  // Estados de carga y errores
  if (fabric === null) {
    return <div className={styles.loading}>Cargando datos de la tela...</div>;
  }

  if (!fabric.code || fabric.code !== code) {
    return (
      <div className={styles.notFound}>
        <div className={styles.notFoundContent}>
          <i className="pi pi-exclamation-triangle" />
          <h2>Tela no encontrada</h2>
          <p>
            El código "{code}" no corresponde a ninguna tela en nuestro
            catálogo.
          </p>
          <Button
            label="Volver al catálogo"
            icon="pi pi-arrow-left"
            onClick={() => navigate("/")}
          />
        </div>
      </div>
    );
  }

  return loading ? (
    <Loader />
  ) : (
    <>
      <div className={styles.fabricDetail}>
        <div className={styles.menubar}>
          <Button
            label="Inicio"
            icon="pi pi-home"
            onClick={() => navigate("/")}
            className={styles.homeButton}
          />
        </div>
        <div className={styles.content}>
          {/* Header Card */}
          <Card className={styles.fabricCard}>
            <div className={styles.contentGrid}>
              <div className={styles.infoSection}>
                <div className={styles.basicInfo}>
                  <h2>{fabric.name}</h2>
                  <p className={styles.code}>{fabric.code}</p>
                  <Tag
                    value={fabric.restriction}
                    severity={getRestrictionSeverity(fabric.restriction)}
                    className={styles.restrictionTag}
                  />
                </div>

                <div className={styles.composition}>
                  <h4>
                    <i className="pi pi-tags" /> Composición
                  </h4>
                  <div className={styles.compositionChips}>
                    {compositionChips.map((comp, i) => (
                      <Chip
                        key={i}
                        label={`${comp.material}${
                          comp.percentage ? ` ${comp.percentage}` : ""
                        }`}
                        className={styles.compositionChip}
                      />
                    ))}
                  </div>
                </div>

                <div className={styles.composition}>
                  <h4>
                    <i className="pi pi-slack" /> Acabados
                  </h4>
                  {finishes.length > 0 ? (
                    <div className={styles.compositionChips}>
                      {finishes.map((finish, i) => (
                        <Chip
                          key={i}
                          label={finish}
                          className={styles.compositionChip}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noFinishes}>
                      Sin acabados disponibles
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.specsSection}>
                <h3>
                  <i className="pi pi-cog" /> Especificaciones
                </h3>
                <div className={styles.specsGrid}>
                  <div className={styles.specItem}>
                    <div className={styles.specIcon}>
                      <i className="pi pi-chart-bar" />
                    </div>
                    <div className={styles.specInfo}>
                      <span className={styles.specLabel}>Peso</span>
                      <span className={styles.specValue}>
                        {fabric.weight} g/m²
                      </span>
                    </div>
                  </div>
                  <div className={styles.specItem}>
                    <div className={styles.specIcon}>
                      <i className="pi pi-arrows-h" />
                    </div>
                    <div className={styles.specInfo}>
                      <span className={styles.specLabel}>Ancho</span>
                      <span className={styles.specValue}>{fabric.width} m</span>
                    </div>
                  </div>
                  <div className={styles.specItem}>
                    <div className={styles.specIcon}>
                      <i className="pi pi-percentage" />
                    </div>
                    <div className={styles.specInfo}>
                      <span className={styles.specLabel}>Rendimiento</span>
                      <span className={styles.specValue}>
                        {fabric.yield} m/kg
                      </span>
                    </div>
                  </div>
                  <div className={styles.specItem}>
                    <div className={styles.specIcon}>
                      <i className="pi pi-wrench" />
                    </div>
                    <div className={styles.specInfo}>
                      <span className={styles.specLabel}>Modulo</span>
                      <span className={styles.specValue}>
                        {fabric.module || "No definido"}
                      </span>
                    </div>
                  </div>
                  <div className={styles.specItem}>
                    <div className={styles.specIcon}>
                      <i className="pi pi-calendar" />
                    </div>
                    <div className={styles.specInfo}>
                      <span className={styles.specLabel}>Fecha Liberación</span>
                      <span className={styles.specValue}>
                        {fabric.moduleDate || "No definido"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          {/* Lista de precios con Accordion */}
          <Card className={styles.pricesCard}>
            <h3>
              <i className="pi pi-dollar" /> Lista de Precios
            </h3>
            <Accordion multiple className={styles.pricesAccordion}>
              {priceTableData.map((priceData, idx) => (
                <AccordionTab
                  key={priceData.lista}
                  header={
                    <span className={styles.listHeader}>
                      Lista {priceData.lista}
                    </span>
                  }
                >
                  <DataTable
                    value={[
                      {
                        tipo: "Kg",
                        blanco: priceData.blancoKg,
                        claros: priceData.clarosKg,
                        oscuros: priceData.oscurosKg,
                      },
                      {
                        tipo: "M",
                        blanco: priceData.blancoM,
                        claros: priceData.clarosM,
                        oscuros: priceData.oscurosM,
                      },
                    ]}
                    className={styles.priceDataTable}
                    showGridlines
                  >
                    <Column
                      field="tipo"
                      header=""
                      bodyClassName={styles.priceTypeCol}
                    />
                    <Column
                      field="blanco"
                      header={
                        <span className={styles.colorHeader}>
                          <span
                            className={`${styles.colorIndicator} ${styles.blanco}`}
                          />
                          Blanco
                        </span>
                      }
                      body={(rowData) => priceTemplate(rowData.blanco)}
                      bodyClassName={styles.blancoCol}
                    />
                    <Column
                      field="claros"
                      header={
                        <span className={styles.colorHeader}>
                          <span
                            className={`${styles.colorIndicator} ${styles.claros}`}
                          />
                          Claros
                        </span>
                      }
                      body={(rowData) => priceTemplate(rowData.claros)}
                      bodyClassName={styles.clarosCol}
                    />
                    <Column
                      field="oscuros"
                      header={
                        <span className={styles.colorHeader}>
                          <span
                            className={`${styles.colorIndicator} ${styles.oscuros}`}
                          />
                          Oscuros
                        </span>
                      }
                      body={(rowData) => priceTemplate(rowData.oscuros)}
                      bodyClassName={styles.oscurosCol}
                    />
                  </DataTable>
                </AccordionTab>
              ))}
            </Accordion>
          </Card>
        </div>
      </div>
    </>
  );
};

export default FabricDetail;
